import { useEffect, useState, MutableRefObject } from 'react'

import useIsServer from 'hooks/useIsServer'

type HlsInstance = {
  loadSource: (src: string) => void
  attachMedia: (el: HTMLVideoElement) => void
  startLoad: () => void
  destroy: () => void
}

// Chrome answers "maybe" to canPlayType for HLS but can't actually play it, so
// native playback is only trusted where MSE is absent (iPhone Safari). hls.js
// (~150KB) loads only on MSE browsers. autoStartLoad is off so video segments
// buffer on first play (startLoad), not at render.
const loadVideo = async (
  video: HTMLVideoElement,
  videoSrc: string,
): Promise<HlsInstance | undefined> => {
  const canPlayNatively = video.canPlayType('application/vnd.apple.mpegurl')

  if (!window.MediaSource && canPlayNatively) {
    // eslint-disable-next-line no-param-reassign
    video.src = videoSrc
    return undefined
  }

  const { default: Hls } = await import(/* webpackChunkName: "hls" */ 'hls.js')

  if (!Hls?.isSupported()) {
    // eslint-disable-next-line no-console
    console.error("This is a legacy browser that doesn't support MSE")
    return undefined
  }

  const hls = new Hls({
    enableWorker: true,
    lowLatencyMode: true,
    capLevelToPlayerSize: true,
    backBufferLength: 90,
    autoStartLoad: false,
  })
  hls.loadSource(videoSrc)
  hls.attachMedia(video)
  return hls
}

export const useVideoPlaying = (
  videoRef: MutableRefObject<HTMLVideoElement>,
) => {
  const [videoPlaying, setVideoPlaying] = useState(false)

  useEffect(() => {
    videoRef.current?.addEventListener('play', () => setVideoPlaying(true))
    videoRef.current?.addEventListener('pause', () => setVideoPlaying(false))

    return () => {
      videoRef.current?.removeEventListener('play', () => setVideoPlaying(true))
      videoRef.current?.removeEventListener('pause', () =>
        setVideoPlaying(false),
      )
    }
  }, [videoRef])

  return videoPlaying
}

export const useForcePause = (
  videoRef: MutableRefObject<HTMLVideoElement>,
  forcePause: boolean,
) => {
  useEffect(() => {
    if (videoRef.current != null && forcePause === true) {
      videoRef.current.pause()
    }
  }, [forcePause, videoRef])
}

export const useLoadVideo = (
  videoRef: MutableRefObject<HTMLVideoElement>,
  src: string,
) => {
  const isServer = useIsServer()
  useEffect(() => {
    // loadVideo is async because hls.js loads on demand. Track the resolved
    // instance and a `cancelled` flag so cleanup destroys the right hls even
    // if the effect re-runs before the promise settles. autoStartLoad is off,
    // so segments only buffer once the user plays (startLoad). A play requested
    // before hls attaches is remembered and replayed once it has.
    const video = videoRef.current
    let hls: HlsInstance | undefined
    let cancelled = false
    let loadStarted = false
    let playRequested = false
    const startLoad = () => {
      if (!hls || loadStarted) return
      loadStarted = true
      hls.startLoad()
    }
    const handlePlay = () => {
      playRequested = true
      startLoad()
    }
    const handlePause = () => {
      playRequested = false
    }
    if (video && src) {
      video.addEventListener('play', handlePlay)
      video.addEventListener('pause', handlePause)
      loadVideo(video, src).then((instance) => {
        if (cancelled) {
          instance?.destroy()
        } else {
          hls = instance
          if (playRequested) {
            startLoad()
            video.play()?.catch(() => {})
          }
        }
      })
    }

    return () => {
      cancelled = true
      video?.removeEventListener('play', handlePlay)
      video?.removeEventListener('pause', handlePause)
      if (hls) {
        hls.destroy()
      }
    }
  }, [videoRef, src, isServer])
}
